import { CheckCircle, XCircle, AlertTriangle, HardDrive, Users, FileText, Package, Settings } from 'lucide-react';

/**
 * Restore Preview Dialog
 * Shows what will be restored before confirming
 */
export default function RestorePreviewDialog({ preview, onConfirm, onCancel, loading }) {
  if (!preview) return null;

  const { metadata, entities } = preview;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Restore from Backup
          </h2>
          <p className="text-sm text-slate-600">
            Review what will be restored from this backup
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Backup Info */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="text-xs font-medium text-slate-600 mb-2">Backup Information</div>
            <div className="space-y-1 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Created:</span>
                <span className="font-medium text-slate-900">
                  {new Date(metadata.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Account:</span>
                <span className="font-medium text-slate-900">{metadata.accountName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Version:</span>
                <span className="font-medium text-slate-900">{metadata.appVersion}</span>
              </div>
            </div>
          </div>

          {/* Entities to Restore */}
          <div>
            <div className="text-sm font-medium text-slate-900 mb-3">
              Data to be restored:
            </div>
            <div className="space-y-2">
              {entities.invoices && (
                <div className="flex items-center justify-between p-3 bg-blue-50/60 rounded-lg border border-blue-200/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                      <FileText size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">Invoices</div>
                      <div className="text-xs text-slate-600">
                        {entities.invoices.count} invoices, {entities.invoices.items} items
                      </div>
                    </div>
                  </div>
                  <CheckCircle size={18} className="text-blue-600" />
                </div>
              )}

              {entities.quotations && (
                <div className="flex items-center justify-between p-3 bg-purple-50/60 rounded-lg border border-purple-200/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                      <FileText size={16} className="text-purple-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">Quotations</div>
                      <div className="text-xs text-slate-600">
                        {entities.quotations.count} quotations, {entities.quotations.items} items
                      </div>
                    </div>
                  </div>
                  <CheckCircle size={18} className="text-purple-600" />
                </div>
              )}

              {entities.clients && (
                <div className="flex items-center justify-between p-3 bg-emerald-50/60 rounded-lg border border-emerald-200/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Users size={16} className="text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">Clients</div>
                      <div className="text-xs text-slate-600">
                        {entities.clients.count} clients
                      </div>
                    </div>
                  </div>
                  <CheckCircle size={18} className="text-emerald-600" />
                </div>
              )}

              {entities.products && (
                <div className="flex items-center justify-between p-3 bg-amber-50/60 rounded-lg border border-amber-200/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Package size={16} className="text-amber-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">Products</div>
                      <div className="text-xs text-slate-600">
                        {entities.products.count} products
                      </div>
                    </div>
                  </div>
                  <CheckCircle size={18} className="text-amber-600" />
                </div>
              )}

              {entities.settings && (
                <div className="flex items-center justify-between p-3 bg-slate-50/60 rounded-lg border border-slate-200/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Settings size={16} className="text-slate-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">Settings</div>
                      <div className="text-xs text-slate-600">
                        Account & SMTP settings
                      </div>
                    </div>
                  </div>
                  <CheckCircle size={18} className="text-slate-600" />
                </div>
              )}
            </div>
          </div>

          {/* Important Notice */}
          <div className="p-4 bg-blue-50/60 border border-blue-200/50 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium text-blue-900 mb-1">
                  Safe Restore (Merge Mode)
                </div>
                <div className="text-xs text-blue-700 leading-relaxed">
                  <strong>Nothing will be deleted.</strong> Existing data will be kept. Only new data from the backup will be added. Existing records won't be overwritten.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Restoring...
              </>
            ) : (
              <>
                <HardDrive size={16} />
                Restore Data
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
